// FILE: components/sections/LayoutSection.tsx
// This section documents the foundational layout components and composition patterns.

import React from 'react';
import { SectionContainer, SubSection, ComponentPreview, Card, PropsTable, Container, Stack, Grid } from '../Content';
import { Layout, LayoutHeader, Main, LayoutSidebar, Footer, Content, TwoColumnLayout } from '../Layout';
import { Breadcrumb } from '../Breadcrumb';
import CodeBlock from '../CodeBlock';
import { PropDef } from '../../../types';

const containerProps: PropDef[] = [
    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The content to be placed inside the container.' },
    { name: 'as', type: 'React.ElementType', default: "'div'", description: 'The HTML element to render the container as.' },
    { name: 'className', type: 'string', default: '""', description: 'Additional CSS classes to apply to the container.' },
    { name: 'size', type: "'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | 'screen'", default: "'lg'", description: 'The maximum width of the container.' },
    { name: 'variant', type: "'default' | 'article' | 'main' | 'aside' | 'section'", default: "'default'", description: 'Semantic variant with predefined styling.' },
    { name: 'padding', type: "'none' | 'sm' | 'md' | 'lg' | 'xl'", default: "'md'", description: 'The padding applied to the container.' },
    { name: 'center', type: 'boolean', default: 'true', description: 'Whether to center the container horizontally.' },
];

const layoutProps: PropDef[] = [
    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The layout structure components.' },
    { name: 'className', type: 'string', default: '""', description: 'Additional CSS classes to apply.' },
];

const layoutHeaderProps: PropDef[] = [
    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The header content.' },
    { name: 'className', type: 'string', default: '""', description: 'Additional CSS classes to apply.' },
    { name: 'sticky', type: 'boolean', default: 'false', description: 'Whether the header should stick to the top when scrolling.' },
    { name: 'elevated', type: 'boolean', default: 'false', description: 'Whether to add shadow and border for elevation.' },
];

const layoutSidebarProps: PropDef[] = [
    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The sidebar content.' },
    { name: 'className', type: 'string', default: '""', description: 'Additional CSS classes to apply.' },
    { name: 'width', type: "'sm' | 'md' | 'lg' | 'xl'", default: "'md'", description: 'The width of the sidebar.' },
    { name: 'position', type: "'left' | 'right'", default: "'left'", description: 'The position of the sidebar.' },
    { name: 'sticky', type: 'boolean', default: 'false', description: 'Whether the sidebar should stick when scrolling.' },
];

const twoColumnLayoutProps: PropDef[] = [
    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'Exactly two children: sidebar and main content.' },
    { name: 'className', type: 'string', default: '""', description: 'Additional CSS classes to apply.' },
    { name: 'sidebarPosition', type: "'left' | 'right'", default: "'left'", description: 'The position of the sidebar.' },
    { name: 'gap', type: "'sm' | 'md' | 'lg' | 'xl'", default: "'lg'", description: 'The gap between sidebar and main content.' },
];

const footerProps: PropDef[] = [
    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The footer content.' },
    { name: 'className', type: 'string', default: '""', description: 'Additional CSS classes to apply.' },
    { name: 'elevated', type: 'boolean', default: 'false', description: 'Whether to add shadow and border for elevation.' },
];

const containerCode = `<Container size="lg" variant="article" padding="lg">
  {/* Your page content goes here */}
</Container>`;

const layoutCode = `<Layout>
  <LayoutHeader sticky elevated>
    <Content padding="md">
      <h1>Page Title</h1>
    </Content>
  </LayoutHeader>
  
  <Main>
    <Content padding="lg">
      <h2>Main Content</h2>
      <p>Your content here...</p>
    </Content>
  </Main>
  
  <Footer>
    <Content padding="md">
      <p>Footer content</p>
    </Content>
  </Footer>
</Layout>`;

const twoColumnCode = `<TwoColumnLayout gap="lg">
  <LayoutSidebar sticky>
    <Content padding="md">
      <nav>Sidebar Navigation</nav>
    </Content>
  </LayoutSidebar>
  
  <Content padding="lg">
    <h2>Main Content Area</h2>
    <p>Your main content here...</p>
  </Content>
</TwoColumnLayout>`;

export const LayoutSection: React.FC<{ groupTitle: string; sectionTitle: string; id: string; }> = ({ groupTitle, sectionTitle, id }) => {
    return (
    <SectionContainer id={id}>
        <Breadcrumb group={groupTitle} section={sectionTitle} />
        <h2 className="section-title">Layout & Grid Components</h2>
            <p className="section-subtitle">
                A consistent layout is the foundation of a good user experience. These declarative layout components encapsulate our spacing and responsive design principles, making it fast and easy to build balanced and predictable interfaces.
            </p>
            
            <SubSection title="Layout Composition Components">
                <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    Build complex page layouts using our composition components. These components work together to create professional, responsive layouts.
                </p>
                
                <div className="grid md:grid-cols-2 gap-8">
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4">Basic Page Layout</h4>
                        <ComponentPreview className="h-64">
                            <Layout className="h-full">
                                <LayoutHeader elevated className="border-b">
                                    <Content padding="sm">
                                        <h3 className="text-lg font-bold">Page Header</h3>
                                    </Content>
                                </LayoutHeader>
                                
                                <Main>
                                    <Content padding="md">
                                        <div className="space-y-4">
                                            <div className="h-8 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                            <div className="h-8 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded w-3/4"></div>
                                            <div className="h-8 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded w-1/2"></div>
                                        </div>
                                    </Content>
                                </Main>
                                
                                <Footer>
                                    <Content padding="sm">
                                        <p className="text-sm text-text-secondary dark:text-dark-text-secondary">Footer</p>
                                    </Content>
                                </Footer>
                            </Layout>
                        </ComponentPreview>
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4">Two-Column Layout</h4>
                        <ComponentPreview className="h-64">
                            <Layout className="h-full">
                                <LayoutHeader elevated className="border-b">
                                    <Content padding="sm">
                                        <h3 className="text-lg font-bold">Dashboard</h3>
                                    </Content>
                                </LayoutHeader>
                                
                                <Main>
                                    <TwoColumnLayout gap="md">
                                        <LayoutSidebar>
                                            <Content padding="sm">
                                                <div className="space-y-2">
                                                    <div className="h-6 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                                    <div className="h-6 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                                    <div className="h-6 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                                </div>
                                            </Content>
                                        </LayoutSidebar>
                                        
                                        <Content padding="sm">
                                            <div className="space-y-3">
                                                <div className="h-6 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                                <div className="h-6 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded w-4/5"></div>
                                                <div className="h-6 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded w-2/3"></div>
                                            </div>
                                        </Content>
                                    </TwoColumnLayout>
                                </Main>
                                
                                <Footer elevated>
                                    <Content padding="sm">
                                        <p className="text-sm text-text-secondary dark:text-dark-text-secondary">Footer with elevation</p>
                                    </Content>
                                </Footer>
                            </Layout>
                        </ComponentPreview>
                    </div>
                </div>
                
                <div className="mt-8">
                    <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4">Layout Implementation</h4>
                    <CodeBlock code={layoutCode} />
                </div>
            </SubSection>
            
            <SubSection title="Footer Examples">
                <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    The Footer component provides consistent footer styling with optional elevation and proper spacing.
                </p>
                
                <div className="grid md:grid-cols-2 gap-8 mb-12">
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4">Default Footer</h4>
                        <ComponentPreview className="h-48 overflow-hidden !p-0">
                            <div className="h-full flex flex-col bg-bg-primary dark:bg-dark-bg-primary">
                                <div className="flex-1 p-6">
                                    <div className="h-24 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                </div>
                                <div className="bg-bg-secondary dark:bg-dark-bg-secondary border-t border-border-color dark:border-dark-border-color p-4">
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm text-text-primary dark:text-dark-text-primary font-medium">© 2024 Acutrack. All rights reserved.</p>
                                        <div className="flex gap-4 text-sm text-text-primary dark:text-dark-text-primary">
                                            <a href="#" className="hover:text-brand-orange transition-colors">Privacy</a>
                                            <a href="#" className="hover:text-brand-orange transition-colors">Terms</a>
                                            <a href="#" className="hover:text-brand-orange transition-colors">Contact</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ComponentPreview>
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-4">Elevated Footer</h4>
                        <ComponentPreview className="h-48 !p-0">
                            <div className="h-full flex flex-col bg-bg-primary dark:bg-dark-bg-primary">
                                <div className="flex-1 p-6">
                                    <div className="h-24 bg-bg-tertiary dark:bg-dark-bg-tertiary rounded"></div>
                                </div>
                                <div className="bg-bg-secondary dark:bg-dark-bg-secondary border-t border-border-color dark:border-dark-border-color p-4 shadow-inner">
                                    <div className="flex justify-between items-center">
                                        <p className="text-sm text-text-primary dark:text-dark-text-primary font-medium">© 2024 Acutrack. All rights reserved.</p>
                                        <div className="flex gap-4 text-sm text-text-primary dark:text-dark-text-primary">
                                            <a href="#" className="hover:text-brand-orange transition-colors">Privacy</a>
                                            <a href="#" className="hover:text-brand-orange transition-colors">Terms</a>
                                            <a href="#" className="hover:text-brand-orange transition-colors">Contact</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ComponentPreview>
                    </div>
                </div>
                
                <div className="mt-8 mb-16">
                    <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">Footer Implementation</h4>
                    <CodeBlock code={`<Layout>
  <Main>
    <Content padding="lg">
      <h2>Main Content</h2>
      <p>Your content here...</p>
    </Content>
  </Main>
  
  <Footer elevated>
    <Content padding="md">
      <div className="flex justify-between items-center">
        <p>© 2024 Acutrack. All rights reserved.</p>
        <div className="flex gap-4">
          <a href="#">Privacy</a>
          <a href="#">Terms</a>
          <a href="#">Contact</a>
        </div>
      </div>
    </Content>
  </Footer>
</Layout>`} />
                </div>
            </SubSection>
        
        <SubSection title="<Container />">
                <div className="mt-16">
                    <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-6 max-w-2xl">
                        The enhanced Container component constrains your content to various standard widths with semantic variants and flexible padding options.
                    </p>
                
                <div className="space-y-6">
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">Container Sizes</h4>
                        <ComponentPreview className="flex-col !items-start gap-4">
                            <Container size="xs" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <p className="text-center font-mono text-sm">xs - max-w-xs</p>
                            </Container>
                            <Container size="sm" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <p className="text-center font-mono text-sm">sm - max-w-sm</p>
                            </Container>
                            <Container size="md" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <p className="text-center font-mono text-sm">md - max-w-md</p>
                            </Container>
                            <Container size="lg" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <p className="text-center font-mono text-sm">lg - max-w-lg</p>
                            </Container>
                            <Container size="xl" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <p className="text-center font-mono text-sm">xl - max-w-xl</p>
                            </Container>
                            <Container size="2xl" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <p className="text-center font-mono text-sm">2xl - max-w-2xl</p>
                            </Container>
                        </ComponentPreview>
                    </div>
                    
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">Container Variants</h4>
                        <ComponentPreview className="flex-col !items-start gap-4">
                            <Container variant="article" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <h4 className="font-bold mb-2">Article Container</h4>
                                <p className="text-sm">Perfect for blog posts and long-form content with prose styling.</p>
                            </Container>
                            <Container variant="section" className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                                <h4 className="font-bold mb-2">Section Container</h4>
                                <p className="text-sm">Ideal for page sections with vertical padding.</p>
                </Container>
            </ComponentPreview>
                    </div>
                </div>
                
            <CodeBlock code={containerCode} />
                <div className="mt-8 mb-12"><PropsTable data={containerProps} /></div>
                </div>
            </SubSection>

        <SubSection title="<Stack />">
            <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    The Stack component is used to arrange a group of elements in a vertical column with a consistent gap between them. It's the ideal way to manage vertical rhythm without adding manual margins to each element.
            </p>
            <ComponentPreview>
                <Stack gap={6} className="w-full max-w-md">
                   <Card isInteractive={false}><Card.Body><p>Item 1</p></Card.Body></Card>
                   <Card isInteractive={false}><Card.Body><p>Item 2</p></Card.Body></Card>
                   <Card isInteractive={false}><Card.Body><p>Item 3</p></Card.Body></Card>
                </Stack>
            </ComponentPreview>
                <CodeBlock code={`<Stack gap={6}>
  <Card>...</Card>
  <Card>...</Card>
  <Card>...</Card>
</Stack>`} />
                <div className="mt-8 mb-12"><PropsTable data={[
                    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The elements to be stacked vertically.' },
                    { name: 'gap', type: '1 | 2 | 3 | 4 | 6 | 8 | ...', default: '4', description: 'The spacing gap between children, based on the 8-point system.' },
                    { name: 'as', type: 'React.ElementType', default: "'div'", description: 'The HTML element to render as.' },
                ]} /></div>
        </SubSection>
        
        <SubSection title="<Grid />">
            <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                    The Grid component is a declarative way to create responsive grid layouts. Pass a number to `columns` for a simple grid, or an object for a grid that adapts to different breakpoints.
            </p>
            <ComponentPreview>
                <Grid columns={{ sm: 2, lg: 3 }} gap={6} className="w-full">
                   <Card><Card.Body><p>Item 1</p></Card.Body></Card>
                   <Card><Card.Body><p>Item 2</p></Card.Body></Card>
                   <Card><Card.Body><p>Item 3</p></Card.Body></Card>
                   <Card><Card.Body><p>Item 4</p></Card.Body></Card>
                   <Card><Card.Body><p>Item 5</p></Card.Body></Card>
                   <Card><Card.Body><p>Item 6</p></Card.Body></Card>
                </Grid>
            </ComponentPreview>
                <CodeBlock code={`<Grid columns={{ sm: 2, lg: 3 }} gap={6}>
  <Card>...</Card>
  <Card>...</Card>
  <Card>...</Card>
  <Card>...</Card>
  <Card>...</Card>
  <Card>...</Card>
</Grid>`} />
                <div className="mt-8 mb-12"><PropsTable data={[
                    { name: 'children', type: 'React.ReactNode', default: 'N/A', description: 'The elements to be placed in the grid.' },
                    { name: 'columns', type: 'number | { sm?, md?, lg?, xl? }', default: '1', description: 'Number of columns, or an object for responsive columns (e.g., { sm: 2, md: 3 }).' },
                    { name: 'gap', type: '1 | 2 | 3 | 4 | 6 | 8 | ...', default: '8', description: 'The spacing gap between grid items.' },
                    { name: 'as', type: 'React.ElementType', default: "'div'", description: 'The HTML element to render as.' },
                ]} /></div>
            </SubSection>
            
            <SubSection title="Component Props">
                <div className="space-y-12">
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<Layout />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={layoutProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<LayoutHeader />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={layoutHeaderProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<LayoutSidebar />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={layoutSidebarProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<TwoColumnLayout />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={twoColumnLayoutProps} /></div>
                    </div>
                    <div>
                        <h4 className="font-bold mb-2 text-text-primary dark:text-dark-text-primary">{'<Footer />'}</h4>
                        <div className="component-preview !p-0 !bg-transparent !border-none"><PropsTable data={footerProps} /></div>
                    </div>
                </div>
            </SubSection>

            <SubSection title="Implementation Examples">
                <div className="space-y-6">
                    <div>
                        <h4 className="font-bold text-text-primary dark:text-dark-text-primary mb-3">Two-Column Layout</h4>
                        <CodeBlock code={twoColumnCode} />
                    </div>
                </div>
        </SubSection>
    </SectionContainer>
);
};
